import { useState, useMemo } from 'react';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { orderBy as lodashOrderBy } from 'lodash';

import {
  Box,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Tooltip,
  Typography,
  TextField,
  TableRow
} from '@mui/material';
import Scrollbar from '../../../components/Scrollbar';
import SearchNotFound from '../../../components/SearchNotFound';
import { UserListHead } from '../../../components/_dashboard/user';
import { RootStyle, SearchStyle, StyledTableRow, StyledTableCell } from '../../Global/Styling';

// ── Table column headers ───────────────────────────────────────────────
const TABLE_HEAD = [
  { id: 'referenceId', label: 'Reference ID' },
  { id: 'date', label: 'Date' },
  { id: 'description', label: 'Description' },
  { id: 'debit', label: 'Debit (₹)' },
  { id: 'credit', label: 'Credit (₹)' },
  { id: 'balance', label: 'Balance (₹)' },
  { id: 'remarks', label: 'Remarks' }
];

// ── Mock Table Data ──────────────────────────────────────────────────
const MOCK_PL_ROWS = [
  {
    referenceId: 'PL-5001',
    date: '2024-02-01',
    description: 'Loan Disbursement Commission',
    debit: 0,
    credit: 125000,
    balance: 125000,
    remarks: 'HDFC Payout'
  },
  {
    referenceId: 'PL-5002',
    date: '2024-02-05',
    description: 'Office Electricity Bill',
    debit: 4500,
    credit: 0,
    balance: 120500,
    remarks: 'Monthly utility'
  },
  {
    referenceId: 'PL-5003',
    date: '2024-02-10',
    description: 'Staff Salaries',
    debit: 85000,
    credit: 0,
    balance: 35500,
    remarks: 'Jan salaries'
  },
  {
    referenceId: 'PL-5004',
    date: '2024-02-15',
    description: 'Consultancy Income',
    debit: 0,
    credit: 25000,
    balance: 60500,
    remarks: 'B2B Client'
  }
];

// ── Static mock KPI data ─────────────────────────────────────────────
const MOCK_KPI = [
  {
    id: 'earning',
    label: 'Total Earning',
    value: '₹ 1,50,000',
    icon: <TrendingUpIcon sx={{ fontSize: '1.8rem', color: '#38a169' }} />,
    bgColor: '#f0fff4',
    borderColor: '#38a169'
  },
  {
    id: 'expense',
    label: 'Total Expense',
    value: '₹ 89,500',
    icon: <TrendingDownIcon sx={{ fontSize: '1.8rem', color: '#e53e3e' }} />,
    bgColor: '#fff5f5',
    borderColor: '#e53e3e'
  },
  {
    id: 'foreclosure',
    label: 'Foreclosure Charges',
    value: '₹ 2,400',
    icon: <CurrencyRupeeIcon sx={{ fontSize: '1.8rem', color: '#d97706' }} />,
    bgColor: '#fffbeb',
    borderColor: '#d97706'
  },
  {
    id: 'netPayable',
    label: 'Net Profit',
    value: '₹ 60,500',
    icon: <AccountBalanceWalletIcon sx={{ fontSize: '1.8rem', color: '#3182ce' }} />,
    bgColor: '#ebf8ff',
    borderColor: '#3182ce'
  }
];

export default function ProfitLoss() {
  const [filterName, setFilterName] = useState('');
  const [isOpenFilter, setOpenFilter] = useState(false);

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  // ── Local Filtering & Sorting Logic ──
  const sortedAndFilteredRows = useMemo(() => {
    let data = [...MOCK_PL_ROWS];

    // 1. Filter
    if (filterName) {
      data = data.filter(
        (row) =>
          row.referenceId.toLowerCase().includes(filterName.toLowerCase()) ||
          row.description.toLowerCase().includes(filterName.toLowerCase())
      );
    }

    // 2. Sort
    return lodashOrderBy(data, [orderBy], [order]);
  }, [filterName, order, orderBy]);

  const isNotFound = sortedAndFilteredRows.length === 0;

  return (
    <Box>
      {/* ── KPI Cards ── */}
      <Grid container spacing={2} mb={3}>
        {MOCK_KPI.map((kpi) => (
          <Grid key={kpi.id} item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                borderLeft: `4px solid ${kpi.borderColor}`,
                backgroundColor: kpi.bgColor
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" sx={{ color: '#718096', fontWeight: 500 }}>
                    {kpi.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748', mt: 0.3 }}>
                    {kpi.value}
                  </Typography>
                </Box>
                {kpi.icon}
              </Stack>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Main Data Card (Toolbar + Table) ── */}
      <Card>
        <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SearchStyle
              value={filterName}
              onChange={handleFilterByName}
              placeholder="Search by ref ID, description…"
              startAdornment={
                <InputAdornment position="start">
                  <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              }
            />
            <Tooltip title="Filter list">
              <IconButton onClick={() => setOpenFilter(!isOpenFilter)}>
                <Icon icon={roundFilterList} />
              </IconButton>
            </Tooltip>
          </Stack>
        </RootStyle>

        {isOpenFilter && (
          <Box px={3} pt={2} pb={2} sx={{ borderBottom: '1px solid #eaedf1' }}>
            <Typography variant="body2" color="text.secondary">
              Advanced filters (Date range picker) placeholder...
            </Typography>
          </Box>
        )}

        <Scrollbar>
          <TableContainer sx={{ minWidth: 800, height: '50vh', overflow: 'auto' }}>
            <Table stickyHeader size="small">
              <UserListHead
                order={order}
                orderBy={orderBy}
                headLabel={TABLE_HEAD}
                rowCount={sortedAndFilteredRows.length}
                onRequestSort={handleRequestSort}
              />

              <TableBody>
                {sortedAndFilteredRows.map((row, index) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>{row.referenceId}</StyledTableCell>
                    <StyledTableCell>{row.date}</StyledTableCell>
                    <StyledTableCell>{row.description}</StyledTableCell>
                    <StyledTableCell>
                      <span className={row.debit !== 0 ? 'acct-badge--red' : ''}>
                        {row.debit === 0 ? '0' : row.debit.toLocaleString()}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell>
                      <span className={row.credit !== 0 ? 'acct-badge--green' : ''}>
                        {row.credit === 0 ? '0' : row.credit.toLocaleString()}
                      </span>
                    </StyledTableCell>
                    <StyledTableCell sx={{ fontWeight: 600 }}>
                      {row.balance.toLocaleString()}
                    </StyledTableCell>
                    <StyledTableCell>{row.remarks}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={7} sx={{ py: 4 }}>
                      <SearchNotFound searchQuery={filterName} />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
    </Box>
  );
}
